/**
 * Tema dell'interfaccia: i tre stati, la preferenza salvata, l'applicazione al
 * documento.
 *
 * Sta fuori dal provider perché è la parte **senza React**: funzioni pure, due
 * accessi al `localStorage` e due scritture sull'elemento radice. Sono le
 * stesse regole che decidono che cosa vede l'utente al primo millisecondo di
 * visita, quindi vanno potute leggere e provare da sole — esattamente come
 * `i18n/language.ts` fa per la lingua, e di proposito con la stessa forma.
 *
 * ## Perché tre stati e non due
 *
 * «Chiaro» e «scuro» sono scelte; «sistema» è l'assenza di scelta, e non è la
 * stessa cosa di «chiaro». Chi tiene il computer in scuro la sera e in chiaro
 * di giorno vuole che la dashboard lo segua; chi ha scelto esplicitamente vuole
 * che non lo segua. Un selettore a due stati costringe a decidere una volta per
 * sempre e perde il caso più comune.
 *
 * **La preferenza esplicita vince sempre sul sistema**, come per la lingua: chi
 * ha scelto ha scelto, anche da un computer configurato al contrario.
 *
 * ## Perché il tema si applica anche al documento, e non solo ad antd
 *
 * `ConfigProvider` colora i componenti di antd. Non colora la pagina intorno,
 * né le scrollbar, né i controlli nativi (`<input type="date">`, il menu di
 * contesto, la selezione del testo). Quelli li decide `color-scheme`, che è una
 * proprietà CSS che il browser legge sull'elemento radice. Applicarla è ciò che
 * distingue un tema scuro da una pagina scura con le barre di scorrimento
 * bianche.
 *
 * `data-theme` accanto serve al foglio di stile e ai test: dice quale tema è
 * attivo in un modo che si può interrogare senza montare React.
 */

/** I tre stati del selettore, nell'ordine in cui compaiono. */
export const THEME_MODES = ['system', 'light', 'dark'] as const;

/** Stato scelto dall'utente: «segui il sistema», «chiaro» o «scuro». */
export type ThemeMode = (typeof THEME_MODES)[number];

/** Tema effettivamente applicato, una volta risolto «sistema». */
export type ResolvedTheme = 'light' | 'dark';

/**
 * Stato iniziale quando l'utente non ha mai scelto.
 *
 * È «sistema» e non «chiaro»: prima di una scelta esplicita la preferenza che
 * conta è quella che l'utente ha già espresso al sistema operativo.
 */
export const DEFAULT_THEME_MODE: ThemeMode = 'system';

/** Chiave sotto cui il browser ricorda il tema scelto. */
export const THEME_STORAGE_KEY = 'institor.theme';

/** Attributo dell'elemento radice che dichiara il tema attivo. */
export const THEME_ATTRIBUTE = 'data-theme';

/** Media query con cui il sistema dichiara di preferire il tema scuro. */
export const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Indica se un valore qualsiasi è uno dei tre stati del tema.
 *
 * @param value Valore da verificare, tipicamente letto dal browser.
 * @returns True se il valore è uno stato valido.
 */
export function isThemeMode(value: unknown): value is ThemeMode {
  return typeof value === 'string' && (THEME_MODES as readonly string[]).includes(value);
}

/**
 * Legge il tema scelto in una visita precedente.
 *
 * Il `localStorage` non è sempre disponibile — finestra anonima, dati del sito
 * bloccati, `SecurityError` dentro un iframe — e leggerlo può sollevare invece
 * di restituire `null`. Un'eccezione qui non deve impedire di mostrare la
 * dashboard: senza preferenza si segue il sistema.
 *
 * @returns Il tema salvato, oppure `null` se non c'è o non è leggibile.
 */
export function readStoredThemeMode(): ThemeMode | null {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeMode(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Ricorda il tema scelto per le visite successive.
 *
 * È una comodità, non un dato: se la scrittura fallisce la scelta resta attiva
 * per questa sessione e basta.
 *
 * @param mode Stato scelto dall'utente.
 */
export function storeThemeMode(mode: ThemeMode): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // Spazio esaurito o accesso negato: la scelta vale comunque fino al
    // ricaricamento della pagina.
  }
}

/**
 * Stato con cui avviare l'applicazione.
 *
 * @returns Il tema salvato, oppure «sistema».
 */
export function initialThemeMode(): ThemeMode {
  return readStoredThemeMode() ?? DEFAULT_THEME_MODE;
}

/**
 * Indica se il sistema operativo preferisce il tema scuro.
 *
 * `matchMedia` manca in jsdom e in qualunque ambiente senza browser: in sua
 * assenza si risponde «no», che è il valore neutro.
 *
 * @returns True se il sistema preferisce il tema scuro.
 */
export function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(DARK_QUERY).matches;
}

/**
 * Risolve i tre stati del selettore nei due temi che esistono davvero.
 *
 * @param mode Stato scelto dall'utente.
 * @param systemDark True se il sistema preferisce il tema scuro.
 * @returns Il tema da applicare.
 */
export function resolveTheme(mode: ThemeMode, systemDark: boolean): ResolvedTheme {
  if (mode === 'system') {
    return systemDark ? 'dark' : 'light';
  }
  return mode;
}

/**
 * Dichiara il tema attivo all'elemento radice del documento.
 *
 * Due scritture, con due destinatari diversi: `data-theme` parla al nostro
 * foglio di stile e ai test, `color-scheme` parla al browser ed è ciò che fa
 * seguire il tema a scrollbar, controlli nativi e fondo della pagina prima che
 * il CSS dell'applicazione sia arrivato.
 *
 * @param resolved Tema effettivamente applicato.
 */
export function applyTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.setAttribute(THEME_ATTRIBUTE, resolved);
  root.style.colorScheme = resolved;
}

/**
 * Legge la preferenza e la applica, in modo sincrono, all'avvio.
 *
 * Va chiamata **prima** del primo render: è l'unica cosa che impedisce il lampo
 * di tema sbagliato, cioè la pagina che compare chiara e diventa scura un
 * fotogramma dopo. Non è asincrona e non aspetta nulla, quindi non ha bisogno
 * di essere coordinata con il caricamento della lingua.
 *
 * @returns Il tema applicato, utile a chi deve inizializzare uno stato con lo
 *   stesso valore.
 */
export function applyInitialTheme(): ResolvedTheme {
  const resolved = resolveTheme(initialThemeMode(), systemPrefersDark());
  applyTheme(resolved);
  return resolved;
}
